import momentUtils from '@date-io/moment';
import { Box, ClickAwayListener, InputAdornment, TextField } from '@material-ui/core';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import CancelIcon from '@material-ui/icons/Cancel';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { CustomTypography } from 'components/Form/Input/Input.styled';
import CalendarFilled from 'icons/CalendarFilled';
import moment, { Moment } from 'moment';
import React, { Component, Fragment, MouseEvent } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import {
  Arrow,
  ClearDateButton,
  ColumnDivider,
  DateButton,
  DayWrapper,
  RangeDatePickerButton,
  RangeDatepickerFieldContainer,
  RangeDatepickerInput,
  RangeDatepickerInputContainer,
  RangeDatepickerPopper,
  RangeDatepickerPopperContainer,
} from './RangeDatepicker.styled';

interface IState {
  anchorEl: Element | null;
}

interface IProps {
  dateFrom?: Date;
  dateTo?: Date;
  onChange: (dateFrom?: Date, dateTo?: Date) => void;
  label?: string;
}

type PropType = IProps & WithTranslation;

class RangeDatepicker extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);

    this.state = {
      anchorEl: null,
    };
  }

  handleClick = (event: MouseEvent): void => {
    const { anchorEl } = this.state;
    const { dateFrom, dateTo, onChange } = this.props;
    this.setState({ anchorEl: anchorEl ? null : event.currentTarget });
    if (!(dateFrom && dateTo)) {
      onChange(moment().toDate(), moment().toDate());
    }
  };

  handleClose = (): void => {
    this.setState({ anchorEl: null });
  };

  handleClearDate = (): void => {
    const { onChange } = this.props;

    onChange(undefined, undefined);
    this.handleClose();
  };

  renderDay = (
    day: MaterialUiPickersDate,
    selectedDate: MaterialUiPickersDate,
    dayInCurrentMonth: boolean,
    dayComponent: JSX.Element,
  ): JSX.Element => {
    const { dateFrom, dateTo } = this.props;
    const isHighlighted =
      dayInCurrentMonth && moment(day).add(1, 'days').isSameOrAfter(dateFrom) && moment(day).isSameOrBefore(dateTo);
    const isFirstHighlighted =
      (isHighlighted && (moment(day).isSame(dateFrom, 'day') || moment(day).day() === 1)) ||
      moment(day).isSame(moment(day).startOf('month'), 'day');
    const isLastHighlighted =
      (isHighlighted && (moment(day).isSame(dateTo, 'day') || moment(day).day() === 0)) ||
      moment(day).isSame(moment(day).endOf('month'), 'day');
    const isSelected = moment(day).isSame(dateFrom, 'day') || moment(day).isSame(dateTo, 'day');
    const isBold = isSelected || moment(day).day() === 0 || moment(day).day() === 6;
    return (
      <DayWrapper
        isHighlighted={isHighlighted}
        isFirstHighlighted={isFirstHighlighted}
        isLastHighlighted={isLastHighlighted}
        isSelected={isSelected}
        isBold={isBold}
      >
        {dayComponent}
      </DayWrapper>
    );
  };

  render(): JSX.Element {
    const { anchorEl } = this.state;
    const { t, dateFrom, dateTo, onChange, label } = this.props;

    moment.updateLocale('en', { week: { dow: 1 } });

    return (
      <RangeDatepickerFieldContainer>
        <CustomTypography color="textSecondary" variant="subtitle2">
          {t(`queue.filters.${label}`)}
        </CustomTypography>
        <ClickAwayListener onClickAway={this.handleClose}>
          <div>
            <RangeDatepickerInputContainer>
              <RangeDatepickerInput>
                {(dateFrom || dateTo) && (
                  <Fragment>
                    {dateFrom ? moment(dateFrom).format('DD - MM - YYYY') : '-'}
                    <span>|</span>
                    {dateTo ? moment(dateTo).format('DD - MM - YYYY') : '-'}
                    <ClearDateButton size="small" onClick={this.handleClearDate}>
                      <CancelIcon />
                    </ClearDateButton>
                  </Fragment>
                )}
              </RangeDatepickerInput>
              <RangeDatePickerButton onClick={this.handleClick}>
                <CalendarTodayIcon />
                {!!anchorEl && <Arrow />}
              </RangeDatePickerButton>
            </RangeDatepickerInputContainer>
            <RangeDatepickerPopper open={!!anchorEl} anchorEl={anchorEl} placement="top-end" disablePortal={true}>
              <RangeDatepickerPopperContainer elevation={6}>
                <MuiPickersUtilsProvider utils={momentUtils}>
                  <Box display="flex">
                    <Box display="flex" flexDirection="column">
                      <TextField
                        variant="outlined"
                        margin="dense"
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarFilled />
                            </InputAdornment>
                          ),
                        }}
                        value={moment(dateFrom).format('DD - MM - YYYY')}
                      />
                      <DatePicker
                        autoOk
                        disableToolbar={true}
                        variant="static"
                        openTo="date"
                        value={dateFrom}
                        renderDay={this.renderDay}
                        shouldDisableDate={(date): boolean => moment(date).isAfter(dateTo)}
                        onChange={(value): void => {
                          onChange((value as Moment).toDate(), dateTo);
                        }}
                      />
                    </Box>
                    <ColumnDivider />
                    <Box display="flex" flexDirection="column">
                      <TextField
                        variant="outlined"
                        margin="dense"
                        InputProps={{
                          readOnly: true,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CalendarFilled />
                            </InputAdornment>
                          ),
                        }}
                        value={moment(dateTo).format('DD - MM - YYYY')}
                      />
                      <DatePicker
                        autoOk
                        disableToolbar={true}
                        variant="static"
                        openTo="date"
                        value={dateTo}
                        renderDay={this.renderDay}
                        shouldDisableDate={(date): boolean => moment(date).add(1, 'days').isBefore(dateFrom)}
                        onChange={(value): void => {
                          onChange(dateFrom, (value as Moment).toDate());
                        }}
                      />
                    </Box>
                    <Box display="flex" flexDirection="column" justifyContent="space-between" marginLeft="16px">
                      <DateButton size="small" onClick={(): void => onChange(moment().toDate(), moment().toDate())}>
                        {t('queue.filters.dates.today')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void =>
                          onChange(moment().subtract(1, 'days').toDate(), moment().subtract(1, 'days').toDate())
                        }
                      >
                        {t('queue.filters.dates.yesterday')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().toDate(), moment().add(14, 'days').toDate())}
                      >
                        {t('queue.filters.dates.next14Days')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().toDate(), moment().add(1, 'months').toDate())}
                      >
                        {t('queue.filters.dates.nextMonth')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().toDate(), moment().add(1, 'years').toDate())}
                      >
                        {t('queue.filters.dates.nextYear')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().subtract(1, 'months').toDate(), moment().toDate())}
                      >
                        {t('queue.filters.dates.lastMonth')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().subtract(14, 'days').toDate(), moment().toDate())}
                      >
                        {t('queue.filters.dates.last14Days')}
                      </DateButton>
                      <DateButton
                        size="small"
                        onClick={(): void => onChange(moment().subtract(1, 'years').toDate(), moment().toDate())}
                      >
                        {t('queue.filters.dates.lastYear')}
                      </DateButton>
                    </Box>
                  </Box>
                </MuiPickersUtilsProvider>
              </RangeDatepickerPopperContainer>
            </RangeDatepickerPopper>
          </div>
        </ClickAwayListener>
      </RangeDatepickerFieldContainer>
    );
  }
}

export default withTranslation()(RangeDatepicker);
